import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthState } from '../../features/auth/domain/useAuthState';

type ReaderSiteHeaderProps = {
  mode: 'stories' | 'notext' | 'reader';
  title?: string;
  subtitle?: string;
};

export default function ReaderSiteHeader({ mode, title, subtitle }: ReaderSiteHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, isAuthenticated, username, providerLabel, logout } = useAuthState();
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const next = `${location.pathname}${location.search}${location.hash}`;
  const state = (location.state ?? {}) as { fromPath?: string };
  const backTarget = state.fromPath || '/stories';

  const handleLogout = async () => {
    setLogoutBusy(true);
    setErrorMsg(null);

    try {
      await logout();
      navigate('/stories', { replace: false });
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Could not complete logout. Please try again.';

      setErrorMsg(message);
    } finally {
      setLogoutBusy(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        backdropFilter: 'blur(14px)',
        background: 'linear-gradient(180deg, rgba(18,15,22,0.96), rgba(18,15,22,0.84))',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Container maxWidth="lg" sx={{ py: 2.25 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                {mode === 'reader' && (
                  <Button
                    variant="text"
                    startIcon={<ArrowBackOutlinedIcon />}
                    onClick={() => navigate(backTarget)}
                    sx={{ mr: 1, minWidth: 0, px: 0.5 }}
                  >
                    Back
                  </Button>
                )}
                <AutoStoriesOutlinedIcon color="primary" fontSize="small" />
                <Typography variant="overline" sx={{ letterSpacing: 1.2, color: 'text.secondary' }}>
                  Nando Reader
                </Typography>
              </Stack>
              {title && (
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={mode === 'stories' ? 'contained' : 'outlined'}
                  startIcon={<MenuBookOutlinedIcon />}
                  onClick={() => navigate('/stories')}
                >
                  Stories
                </Button>
                <Button
                  variant={mode === 'notext' ? 'contained' : 'outlined'}
                  startIcon={<VisibilityOffOutlinedIcon />}
                  onClick={() => navigate('/stories-notext')}
                >
                  No Text
                </Button>
              </Stack>

              {loading ? (
                <Button variant="text" disabled startIcon={<CircularProgress size={16} color="inherit" />}>
                  Checking session
                </Button>
              ) : isAuthenticated ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    label={username ? `Signed in as ${username}` : `Signed in${providerLabel ? ` via ${providerLabel}` : ''}`}
                    color="primary"
                    variant="outlined"
                  />
                  <Button
                    variant="text"
                    color="inherit"
                    startIcon={logoutBusy ? <CircularProgress size={16} color="inherit" /> : <LogoutOutlinedIcon />}
                    disabled={logoutBusy}
                    onClick={() => void handleLogout()}
                  >
                    Log out
                  </Button>
                </Stack>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<LoginOutlinedIcon />}
                  onClick={() => navigate(`/login?next=${encodeURIComponent(next)}`)}
                >
                  Sign in
                </Button>
              )}
            </Stack>
          </Stack>

          {mode === 'reader' && (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" label="Reader mode" color="primary" variant="outlined" />
                <Chip size="small" label="Chapter navigation enabled" variant="outlined" />
              </Stack>
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                Use the chapter panel below to switch chapters without leaving the reader.
              </Typography>
            </Stack>
          )}

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

          {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
        </Stack>
      </Container>
    </Box>
  );
}
